import styled from '@emotion/styled'
import {Link} from 'react-router-dom'
import sideNavBg from '../../core/assets/sideNav/sideNav-bg.jpg'

export const SIDE_NAV_EXPANDED_WIDTH = 245
export const SIDE_NAV_COLLAPSED_WIDTH = 64

const ACTIVE_BG = '#ff5000'
const TEXT = 'rgba(255, 255, 255, 0.78)'

// Style-only props that must not reach the DOM / Link.
const STYLE_PROPS = new Set(['isCollapsed', 'isMobile', 'isActive', 'variant'])
const shouldForwardProp = prop => !STYLE_PROPS.has(prop)
const styledEl = tag => styled(tag, {shouldForwardProp})

export const NavShell = styledEl('aside')`
  grid-area: sidebar;
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: ${p =>
    p.isCollapsed ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_EXPANDED_WIDTH}px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  color: ${TEXT};
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  z-index: ${p => (p.isMobile ? 999 : 99)};
`

// Clipped background layer. It owns the base gradient so the Hue-blended art
// below blends with it (and the clip doesn't cut off the collapsed popout,
// which lives outside this layer).
export const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  overflow: hidden;
  pointer-events: none;
  background: linear-gradient(180deg, #0f1a3c 0%, #232a66 45%, #6a5cf0 100%);

  /* Figma background art: 1320x660, rotated 90deg (Figma angles are
     counter-clockwise = CSS 270deg), Hue-blended over the base gradient.
     Figma's top/left (-166/-98) describe the rotated box, so the unrotated
     element is offset to match: left = -98 + 330 - 660 = -428,
     top = -166 + 660 - 330 = 164. */
  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    top: 200px;
    left: -428px;
    width: 1320px;
    height: 660px;
    background: url(${sideNavBg}) center / 100% 100% no-repeat;
    transform: rotate(-90deg);
    opacity: 1;
    mix-blend-mode: hue;
  }
`

export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`

/* ---------- header ---------- */

// Expanded: hamburger column | brand column. Collapsed: stacked in one column.
export const Header = styledEl('div')`
  flex: none;
  display: flex;
  flex-direction: ${p => (p.isCollapsed ? 'column' : 'row')};
  align-items: ${p => (p.isCollapsed ? 'center' : 'flex-start')};
  gap: ${p => (p.isCollapsed ? '16px' : '20px')};
  padding: ${p => (p.isCollapsed ? '18px 0 12px' : '18px 16px 12px 12px')};
`

// Logo + subtitle stacked in one column.
export const BrandText = styledEl('div')`
  display: flex;
  flex-direction: column;
  align-items: ${p => (p.isCollapsed ? 'center' : 'flex-start')};
`

export const LogoLink = styled(Link)`
  display: block;
  text-decoration: none;
  color: #fff;
`

export const LogoFull = styled.img`
  display: block;
  width: 93px;
  height: 38px;
`

export const LogoT = styled.img`
  display: block;
  width: 12px;
  height: 27px;
`

export const ProductName = styledEl('span')`
  margin-top: ${p => (p.isCollapsed ? '8px' : '2px')};
  font-size: 12px;
  color: #fff;
  white-space: nowrap;
  text-align: center;
  font-family: Open Sans;
  font-weight: 400;
  font-style: Regular;
  font-size: 12px;
  leading-trim: NONE;
  line-height: 100%;
  letter-spacing: 0%;
`

export const ToggleButton = styledEl('button')`
  flex: none;
  margin: 0;
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  display: flex;
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }

  /* Maverick Icon root */
  & > * {
    width: 20px;
  }
`

/* ---------- nav list ---------- */

// The `sidebar-open` / `sidebar-collapse` class names on this element are
// read by styles/global.css (html:has(nav.sidebar-collapse)) — keep them.
export const Nav = styledEl('nav')`
  flex: 1;
  min-height: 0;
  /* collapsed: must not clip the hover popout */
  overflow: ${p => (p.isCollapsed ? 'visible' : 'hidden auto')};
  padding: ${p => (p.isCollapsed ? '8px 15px' : '8px 12px')};
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

/* ---------- items (link / group button / disabled) ---------- */

// main: top-level row (sizes depend on collapsed state)
// sub:  indented row under an expanded group
// popout: row inside the collapsed-state hover popout
const itemVariantStyles = p => {
  switch (p.variant) {
    case 'sub':
      return `
        height: 38px;
        margin: 0 0 4px;
        padding: 0 14px 0 24px;
        font-size: 12px;
      `
    case 'popout':
      return `
        height: 38px;
        margin: 0 0 4px;
        padding: 0 12px;
        font-size: 12px;
      `
    default:
      return `
        height: ${p.isCollapsed ? 34 : 44}px;
        margin: 0 0 ${p.isCollapsed ? 12 : 6}px;
        padding: ${p.isCollapsed ? '0' : '0 14px'};
        justify-content: ${p.isCollapsed ? 'center' : 'flex-start'};
        font-size: 13px;
      `
  }
}

const itemStyles = p => `
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  box-sizing: border-box;
  border: none;
  border-radius: 10px;
  background: ${p.isActive ? ACTIVE_BG : 'transparent'};
  color: ${p.isActive ? '#fff' : TEXT};
  font-family: inherit;
  font-weight: ${p.isActive ? 700 : 500};
  box-shadow: ${p.isActive ? '0px 4px 9.6px 0px #00000073' : 'none'};
  text-align: left;
  text-decoration: none !important;
  cursor: pointer;
  transition: background 0.15s ease;
  ${itemVariantStyles(p)}

  &:hover {
    background: ${p.isActive ? ACTIVE_BG : 'rgba(255, 255, 255, 0.1)'};
    color: #fff;
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`

export const ItemLink = styled(Link, {shouldForwardProp})`
  ${itemStyles}
`

export const ItemButton = styledEl('button')`
  ${itemStyles}
`

export const ItemDisabled = styledEl('div')`
  ${itemStyles}
  opacity: 0.5;
  cursor: not-allowed;

  &:hover {
    background: transparent;
    color: ${TEXT};
  }
`

// Existing icons are dark-grey SVGs/PNGs; force them white on the gradient.
// Drop the filter when the white Figma icons replace them.
export const ItemIcon = styledEl('img')`
  flex: none;
  width: ${p =>
    p.variant === 'sub' || p.variant === 'popout'
      ? 16
      : p.isCollapsed
      ? 18
      : 20}px;
  height: ${p =>
    p.variant === 'sub' || p.variant === 'popout'
      ? 16
      : p.isCollapsed
      ? 18
      : 20}px;
  object-fit: contain;
  filter: brightness(0) invert(1);
`

export const ItemLabel = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: Open Sans;
  font-weight: 400;
  font-style: Regular;
  font-size: 12px;
  leading-trim: NONE;
  // line-height: 100%;
  letter-spacing: 0%;
  color: #ffffff;
  // color: #ffffffb2;
`

export const Chevron = styled.span`
  flex: none;
  display: flex;

  /* Maverick Icon root */
  & > * {
    width: 10px;
  }
`

/* ---------- collapsed: group hover popout ---------- */

export const Group = styled.li`
  position: relative;

  /* the popout is the group's only <div> child */
  &:hover > div,
  &:focus-within > div {
    display: block;
  }
`

export const Popout = styled.div`
  display: none;
  position: absolute;
  left: 100%;
  top: 0;
  padding-left: 8px; /* hover bridge so the popout doesn't close in the gap */
  z-index: 10;
`

export const PopoutPanel = styled(List)`
  min-width: 200px;
  padding: 6px;
  border-radius: 10px;
  background: #1c2457;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
`

/* ---------- footer ---------- */

export const Footer = styledEl('div')`
  flex: none;
  padding: ${p => (p.isCollapsed ? '8px 15px 16px' : '8px 12px 16px')};
`

// Footer actions (Create Keap User, Open Ticket, Refresh Cache) look like the
// regular nav rows (see itemStyles): transparent, muted text, light hover
// wash, never the orange "active" state. They are the shared / third-party
// <Button>, so the overrides need !important. The Keap modal renders in a
// portal so it is not affected.
export const Actions = styledEl('div')`
  display: flex;
  flex-direction: column;
  align-items: ${p => (p.isCollapsed ? 'center' : 'stretch')};
  gap: ${p => (p.isCollapsed ? '12px' : '6px')};

  button {
    display: flex;
    align-items: center;
    justify-content: ${p => (p.isCollapsed ? 'center' : 'flex-start')};
    gap: 14px;
    box-sizing: border-box;
    width: ${p => (p.isCollapsed ? '34px' : '100%')};
    height: ${p => (p.isCollapsed ? '34px' : '44px')};
    padding: ${p => (p.isCollapsed ? '0' : '0 14px')} !important;
    border: none !important;
    border-radius: 10px;
    background: transparent !important;
    box-shadow: none !important;
    color: ${TEXT} !important;
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  button:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1) !important;
    color: #fff !important;
  }

  button:active:not(:disabled) {
    transform: none;
  }

  /* icons follow the text colour (Refresh Cache ships a hard-coded grey stroke) */
  button svg {
    flex: none;
    width: 20px;
    height: 20px;
    stroke: currentColor;
  }

  button:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`

// Non-clickable group heading between nav items (items with `heading: true`).
// Expanded: small uppercase label. Collapsed: a thin separator line.
export const SectionTitle = styledEl('div')`
  ${p =>
    p.isCollapsed
      ? `
    height: 1px;
    margin: 6px 6px 14px;
    background: rgba(255, 255, 255, 0.25);
  `
      : `
    margin: 14px 0 6px;
    padding: 0 14px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.55);
    white-space: nowrap;
  `}
`

export const Divider = styled.div`
  height: 1px;
  margin: 0 0 12px;
  background: rgba(255, 255, 255, 0.2);
`
